import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { Users } from "lucide-react";
import "./HiringFunnelChart.css";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="funnel-tooltip">
        <p className="tooltip-stage">{data.name}</p>
        <p className="tooltip-count">{data.count} candidates</p>
        {data.conversionRate !== null && data.conversionRate !== undefined && (
          <p className="tooltip-conversion">
            {data.conversionRate.toFixed(1)}% conversion from previous
          </p>
        )}
      </div>
    );
  }
  return null;
};

const FunnelSkeleton = () => (
  <div className="funnel-skeleton">
    <div className="funnel-skeleton-bars">
      {[100, 85, 65, 45, 30].map((width, index) => (
        <div key={index} className="funnel-skeleton-bar">
          <div className="funnel-skeleton-label" />
          <div className="funnel-skeleton-track">
            <div
              className="funnel-skeleton-fill"
              style={{ width: `${width}%` }}
            />
          </div>
        </div>
      ))}
    </div>
    <div className="funnel-skeleton-summary">
      <div className="funnel-skeleton-stat">
        <div className="funnel-skeleton-stat-label" />
        <div className="funnel-skeleton-stat-value" />
      </div>
      <div className="funnel-skeleton-stat">
        <div className="funnel-skeleton-stat-label" />
        <div className="funnel-skeleton-stat-value" />
      </div>
    </div>
  </div>
);

const HiringFunnelChart = ({ data, isLoading }) => {
  // Show skeleton while loading
  if (isLoading) {
    return <FunnelSkeleton />;
  }

  // Handle nested data structure from API: { stages: [...], totalApplications, ... }
  const stages = data?.stages || [];

  if (!stages || stages.length === 0) {
    return (
      <div className="funnel-empty">
        <Users className="funnel-empty-icon" />
        <p className="funnel-empty-title">No Funnel Data Yet</p>
        <p className="funnel-empty-message">
          Funnel data will appear here once applications enter your hiring
          workflow.
        </p>
      </div>
    );
  }

  // Calculate conversion rates between stages
  const chartData = stages.map((stage, index) => {
    const previousCount = index > 0 ? stages[index - 1].count : null;
    const conversionRate =
      previousCount && previousCount > 0
        ? (stage.count / previousCount) * 100
        : null;

    return {
      ...stage,
      conversionRate,
    };
  });

  return (
    <div className="funnel-chart-container">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" />
          <YAxis
            dataKey="name"
            type="category"
            width={120}
            tick={{ fontSize: 12, fontFamily: "Fira Sans" }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={28}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || "#1E40AF"} />
            ))}
            <LabelList
              dataKey="count"
              position="right"
              style={{ fontSize: 12, fontFamily: "Fira Code", fill: "#374151" }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Conversion rate summary */}
      <div className="funnel-summary">
        <div className="funnel-summary-item">
          <span className="summary-label">Total in pipeline</span>
          <span className="summary-value">
            {chartData.reduce((sum, stage) => sum + stage.count, 0)}
          </span>
        </div>
        {chartData.length > 1 && chartData[chartData.length - 1].count > 0 && (
          <div className="funnel-summary-item">
            <span className="summary-label">Overall conversion</span>
            <span className="summary-value">
              {(
                (chartData[chartData.length - 1].count / chartData[0].count) *
                100
              ).toFixed(1)}
              %
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HiringFunnelChart;
